import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import type { Reminder, Schedule } from "./types";

const TIMELINE_LEDGER_KEY = "mindback.localNotificationIds";

function numericId(id: string): number {
  let value = 0;
  for (let i = 0; i < id.length; i += 1) value = (value * 31 + id.charCodeAt(i)) >>> 0;
  return value % 2_000_000_000;
}

export async function scheduleAndroidTimeline(reminders: Reminder[], schedules: Schedule[] = [], mutedChannelIds: string[] = []) {
  if (!Capacitor.isNativePlatform()) return { platform: "web", scheduled: 0 };
  const permission = await LocalNotifications.requestPermissions();
  if (permission.display !== "granted") return { platform: "native", scheduled: 0, permission: permission.display };
  const now = Date.now();
  const reminderNotifications = reminders
    .filter((item) => item.status === "scheduled" && new Date(item.remind_at).getTime() > now)
    .filter((item) => !item.channel_id || !mutedChannelIds.includes(item.channel_id))
    .map((item) => ({
      id: numericId(`reminder-${item.id}`),
      title: "MindBack 提醒",
      body: item.title,
      schedule: { at: new Date(item.remind_at), allowWhileIdle: true },
      extra: { reminderId: item.id, messageId: item.message_id || "" }
    }));
  const scheduleNotifications = schedules
    .filter((item) => item.status === "scheduled" && new Date(item.start_at).getTime() > now)
    .filter((item) => !item.channel_id || !mutedChannelIds.includes(item.channel_id))
    .map((item) => {
      const start = new Date(item.start_at).getTime();
      const lead = Math.max(0, item.remind_minutes_before || 0) * 60_000;
      const at = new Date(Math.max(Date.now() + 1000, start - lead));
      return {
        id: numericId(`schedule-${item.id}`),
        title: "MindBack 日程",
        body: item.title,
        schedule: { at, allowWhileIdle: true },
        extra: { scheduleId: item.id, messageId: item.message_id || "" }
      };
    });
  const notifications = [...reminderNotifications, ...scheduleNotifications].slice(0, 64);
  const desiredIds = notifications.map((notification) => notification.id);
  const previousIds = readTimelineLedger();
  const obsolete = previousIds.filter((id) => !desiredIds.includes(id));
  if (obsolete.length > 0) {
    await LocalNotifications.cancel({ notifications: obsolete.map((id) => ({ id })) });
  }
  if (notifications.length > 0) await LocalNotifications.schedule({ notifications });
  localStorage.setItem(TIMELINE_LEDGER_KEY, JSON.stringify(desiredIds));
  return { platform: "native", scheduled: notifications.length, permission: permission.display };
}

export async function clearAndroidTimeline() {
  if (!Capacitor.isNativePlatform()) return;
  const ids = readTimelineLedger();
  if (ids.length > 0) await LocalNotifications.cancel({ notifications: ids.map((id) => ({ id })) });
  localStorage.removeItem(TIMELINE_LEDGER_KEY);
}

function readTimelineLedger(): number[] {
  try {
    const value = JSON.parse(localStorage.getItem(TIMELINE_LEDGER_KEY) || "[]");
    return Array.isArray(value) ? value.filter((id): id is number => Number.isInteger(id)) : [];
  } catch {
    return [];
  }
}

export async function getNotificationStatus() {
  if (Capacitor.isNativePlatform()) {
    const permission = await LocalNotifications.checkPermissions();
    return { platform: "android", permission: permission.display, supported: true };
  }
  if ("Notification" in window) return { platform: "web", permission: Notification.permission, supported: true };
  return { platform: "web", permission: "unsupported", supported: false };
}

export async function requestWebNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    return Notification.requestPermission();
  }
  const status = await getNotificationStatus();
  return status.permission;
}
